import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import UserAvatar from '../components/UserAvatar';
import { useUser } from '../components/UserContext';
import {
  getRoleLabel,
  USER_ROLES,
} from '../constants/userRoles';
import {
  deleteUserAccount,
  updateUserRole,
} from '../services/adminUsersService';
import {
  filterAndSortUsers,
  getValidPage,
  paginateUsers,
  USERS_PAGE_SIZE,
} from './adminUsersUtils';
import { hasEditorialHistory } from '../profile/profileUtils';
import '../styles/AdminUsersPanel.css';

const getErrorMessage = (error, actionType) => {
  const rawCode = String(error?.code || '');
  const code = rawCode.replace(/^functions\//, '');
  const messages = {
    unauthenticated: 'Сесія завершилась. Увійдіть повторно.',
    'permission-denied': 'Лише адміністратор може керувати ролями користувачів.',
    'invalid-argument': 'Передані некоректні дані користувача або ролі.',
    'not-found': 'Користувача не знайдено.',
    'failed-precondition':
      error?.message?.includes('останнього адміністратора')
        ? 'Неможливо змінити роль останнього адміністратора.'
        : 'Цю операцію неможливо виконати в поточному стані.',
    internal: actionType === 'delete'
      ? 'Не вдалося видалити користувача. Спробуйте ще раз.'
      : 'Не вдалося змінити роль. Спробуйте ще раз.',
    unavailable: 'Сервіс тимчасово недоступний. Спробуйте ще раз.',
  };

  return messages[code] || 'Не вдалося виконати операцію. Спробуйте ще раз.';
};

const formatDate = (value) => {
  if (!value) return '—';
  const date = typeof value.toDate === 'function'
    ? value.toDate()
    : new Date(value.seconds ? value.seconds * 1000 : value);

  if (Number.isNaN(date.getTime())) return '—';

  return date.toLocaleDateString('uk-UA', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const actionCopy = (user, action, role) => {
  const name = user.username || user.email || 'цього користувача';

  if (action === 'delete') {
    return {
      title: 'Видалити користувача',
      body: `Ви впевнені, що хочете видалити користувача ${name}? Цю дію неможливо скасувати.`,
      confirm: 'Видалити користувача',
    };
  }

  if (role === USER_ROLES.USER) {
    return {
      title: 'Звільнити працівника',
      body: `Звільнити працівника ${name}? Його роль буде змінено на «Користувач».`,
      confirm: 'Звільнити',
    };
  }

  if (role === USER_ROLES.ADMIN) {
    return {
      title: 'Надати права адміністратора',
      body: `Надати користувачу ${name} права адміністратора?`,
      confirm: 'Зробити адміністратором',
    };
  }

  return {
    title: 'Призначити автора',
    body: `Призначити користувача ${name} автором?`,
    confirm: 'Зробити автором',
  };
};

const AdminUsersPanel = ({ view }) => {
  const { user: currentUser } = useUser();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyUid, setBusyUid] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [pending, setPending] = useState(null);
  const [usersPage, setUsersPage] = useState(1);

  const fetchUsers = useCallback(async () => {
    setError('');
    setLoading(true);

    try {
      const snapshot = await getDocs(collection(db, 'users'));
      setUsers(snapshot.docs
        .map((item) => ({ uid: item.id, ...item.data() }))
        .filter((item) =>
          item.isDeleted !== true && item.deletionPending !== true
        ));
    } catch (fetchError) {
      console.error(fetchError);
      setError('Не вдалося завантажити користувачів.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  const visibleUsers = useMemo(
    () => filterAndSortUsers(users, view),
    [users, view]
  );

  const usersPagination = useMemo(
    () => paginateUsers(visibleUsers, usersPage, USERS_PAGE_SIZE),
    [usersPage, visibleUsers]
  );

  useEffect(() => {
    if (view !== 'users') return;

    const validPage = getValidPage(
      usersPage,
      visibleUsers.length,
      USERS_PAGE_SIZE
    );

    if (validPage !== usersPage) {
      setUsersPage(validPage);
    }
  }, [usersPage, view, visibleUsers.length]);

  const groups = useMemo(() => {
    if (view === 'users') {
      return [
        {
          title: 'Користувачі',
          items: usersPagination.items.filter(
            (item) => !hasEditorialHistory(item)
          ),
        },
        {
          title: 'Колишні працівники',
          items: usersPagination.items.filter(hasEditorialHistory),
        },
      ].filter((group) => group.items.length > 0);
    }

    return [
      {
        title: 'Адміністратори',
        items: visibleUsers.filter((item) => item.role === USER_ROLES.ADMIN),
      },
      {
        title: 'Автори',
        items: visibleUsers.filter((item) => item.role === USER_ROLES.AUTHOR),
      },
    ].filter((group) => group.items.length > 0);
  }, [usersPagination.items, view, visibleUsers]);

  const requestRoleChange = (target, newRole) => {
    setPending({
      type: 'role',
      target,
      newRole,
      ...actionCopy(target, 'role', newRole),
    });
  };

  const requestDelete = (target) => {
    setPending({
      type: 'delete',
      target,
      ...actionCopy(target, 'delete'),
    });
  };

  const confirmAction = async () => {
    if (!pending || busyUid) return;

    const { target } = pending;
    setBusyUid(target.uid);
    setError('');
    setSuccess('');

    try {
      if (pending.type === 'delete') {
        await deleteUserAccount(target.uid);
        setUsers((current) => current.filter((item) => item.uid !== target.uid));
        setSuccess('Користувача видалено.');
      } else {
        const roleResult = await updateUserRole(target.uid, pending.newRole);
        setUsers((current) => current.map((item) => item.uid === target.uid
          ? {
            ...item,
            role: pending.newRole,
            employment: roleResult.employment,
            employmentHistory: roleResult.employmentHistory,
            roleUpdatedAt: new Date(),
          }
          : item));

        const messages = {
          author: 'Користувача призначено автором.',
          admin: 'Користувачу надано права адміністратора.',
          user: 'Працівника звільнено.',
        };
        setSuccess(messages[pending.newRole]);
      }
      setPending(null);
    } catch (actionError) {
      console.error(actionError);
      setError(getErrorMessage(actionError, pending.type));
    } finally {
      setBusyUid('');
    }
  };

  const renderActions = (item) => {
    const isSelf = currentUser?.uid === item.uid;
    const disabled = Boolean(busyUid) || isSelf;

    if (isSelf) {
      return <span className="admin-users-self-note">Ви не можете змінити власну роль</span>;
    }

    if (item.role === USER_ROLES.USER) {
      return (
        <div className="admin-users-actions">
          <button
            className="admin-user-action admin-user-action--author"
            disabled={disabled}
            onClick={() => requestRoleChange(item, USER_ROLES.AUTHOR)}
          >
            Зробити автором
          </button>
          <button
            className="admin-user-action admin-user-action--admin"
            disabled={disabled}
            onClick={() => requestRoleChange(item, USER_ROLES.ADMIN)}
          >
            Зробити адміністратором
          </button>
          <button
            className="admin-user-action admin-user-action--delete"
            disabled={disabled}
            onClick={() => requestDelete(item)}
          >
            Видалити
          </button>
        </div>
      );
    }

    return (
      <div className="admin-users-actions">
        {item.role === USER_ROLES.AUTHOR ? (
          <button
            className="admin-user-action admin-user-action--admin"
            disabled={disabled}
            onClick={() => requestRoleChange(item, USER_ROLES.ADMIN)}
          >
            Зробити адміністратором
          </button>
        ) : (
          <button
            className="admin-user-action admin-user-action--author"
            disabled={disabled}
            onClick={() => requestRoleChange(item, USER_ROLES.AUTHOR)}
          >
            Зробити автором
          </button>
        )}
        <button
          className="admin-user-action admin-user-action--dismiss"
          disabled={disabled}
          onClick={() => requestRoleChange(item, USER_ROLES.USER)}
        >
          Звільнити
        </button>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="admin-users-loading" role="status">
        <span className="admin-users-spinner" />
        Завантажуємо користувачів...
      </div>
    );
  }

  return (
    <section className="admin-users">
      <header className="admin-users-head">
        <div>
          <h2>{view === 'users' ? 'Користувачі' : 'Працівники'}</h2>
          <p>
            {view === 'users'
              ? 'Звичайні користувачі, колишні працівники та призначення ролей.'
              : 'Автори й адміністратори BigSport.'}
          </p>
        </div>
        <button
          className="admin-users-refresh"
          onClick={fetchUsers}
          disabled={Boolean(busyUid)}
        >
          Оновити
        </button>
      </header>

      {error && <div className="admin-users-message admin-users-message--error">{error}</div>}
      {success && <div className="admin-users-message admin-users-message--success">{success}</div>}

      {visibleUsers.length === 0 ? (
        <div className="admin-users-empty">
          {view === 'users'
            ? 'Користувачів поки немає'
            : 'Працівників поки немає'}
        </div>
      ) : groups.map((group) => (
        <div className="admin-users-group" key={group.title}>
          <div className="admin-users-subhead">
            <h3>{group.title}</h3>
            <span>{group.total ?? group.items.length}</span>
          </div>

          <div className="admin-users-table-wrap">
            <table className="admin-users-table">
              <thead>
                <tr>
                  <th>Ім’я</th>
                  <th>Email</th>
                  <th>Роль</th>
                  <th>Створено</th>
                  {view === 'employees' && <th>Призначено</th>}
                  <th>Дії</th>
                </tr>
              </thead>
              <tbody>
                {group.items.map((item) => (
                  <tr key={item.uid}>
                    <td data-label="Ім’я" className="admin-users-name">
                      <Link
                        to={`/profile/${item.uid}`}
                        className="admin-user-profile-link"
                        title="Переглянути профіль"
                      >
                        <UserAvatar
                          photoURL={item.photoURL}
                          name={item.username || item.displayName}
                          email={item.email}
                          size={40}
                        />
                        <span>
                          {item.username || item.displayName || 'Без імені'}
                        </span>
                      </Link>
                    </td>
                    <td data-label="Email">{item.email || '—'}</td>
                    <td data-label="Роль">
                      <span className={`admin-role-badge admin-role-badge--${item.role || 'user'}`}>
                        {getRoleLabel(item.role)}
                      </span>
                    </td>
                    <td data-label="Створено">{formatDate(item.createdAt)}</td>
                    {view === 'employees' && (
                      <td data-label="Призначено">
                        {formatDate(item.roleUpdatedAt || item.hiredAt)}
                      </td>
                    )}
                    <td data-label="Дії">
                      {busyUid === item.uid
                        ? <span className="admin-users-working">Виконуємо...</span>
                        : renderActions(item)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}

      {view === 'users' && usersPagination.totalPages > 1 && (
        <nav className="admin-users-pagination" aria-label="Сторінки користувачів">
          <span className="admin-users-pagination-summary">
            Показано {usersPagination.start}–{usersPagination.end} із{' '}
            {usersPagination.totalItems}
          </span>
          <div className="admin-users-pagination-buttons">
            <button
              className="admin-users-pagination-nav"
              disabled={usersPagination.page === 1}
              onClick={() => setUsersPage((page) => Math.max(1, page - 1))}
            >
              Попередня
            </button>
            {Array.from(
              { length: usersPagination.totalPages },
              (_, index) => index + 1
            ).map((page) => (
              <button
                key={page}
                className={`admin-users-pagination-page ${
                  usersPagination.page === page ? 'active' : ''
                }`}
                aria-current={usersPagination.page === page ? 'page' : undefined}
                onClick={() => setUsersPage(page)}
              >
                {page}
              </button>
            ))}
            <button
              className="admin-users-pagination-nav"
              disabled={usersPagination.page === usersPagination.totalPages}
              onClick={() => setUsersPage((page) =>
                Math.min(usersPagination.totalPages, page + 1)
              )}
            >
              Наступна
            </button>
          </div>
        </nav>
      )}

      {pending && (
        <div className="admin-confirm-backdrop" role="presentation">
          <div
            className="admin-confirm-modal"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-confirm-title"
          >
            <h3 id="admin-confirm-title">{pending.title}</h3>
            <p>{pending.body}</p>
            <div className="admin-confirm-actions">
              <button
                className="admin-confirm-cancel"
                onClick={() => setPending(null)}
                disabled={Boolean(busyUid)}
              >
                Скасувати
              </button>
              <button
                className={pending.type === 'delete'
                  ? 'admin-confirm-delete'
                  : 'admin-confirm-submit'}
                onClick={confirmAction}
                disabled={Boolean(busyUid)}
              >
                {busyUid ? 'Виконуємо...' : pending.confirm}
              </button>
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default AdminUsersPanel;
